import { useCallback, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BulkEditDialog } from "@/components/explorer/BulkEditDialog";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// Row selection for any list (ids of the rows currently shown).
export function useSelection(shownRows) {
  const [sel, setSel] = useState(() => new Set());
  const toggle = useCallback((id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const ids = useMemo(() => shownRows.map((r) => r.id), [shownRows]);
  const allOn = ids.length > 0 && ids.every((id) => sel.has(id));
  const someOn = !allOn && ids.some((id) => sel.has(id));
  const toggleAll = useCallback(() => setSel((s) => { const n = new Set(s); const on = !ids.every((id) => n.has(id)); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; }), [ids]);
  const clear = useCallback(() => setSel(new Set()), []);
  return { sel, toggle, toggleAll, clear, allOn, someOn, count: sel.size };
}

export const SelectAllBox = ({ s, testId = "select-all" }) => (
  <Checkbox checked={s.allOn ? true : s.someOn ? "indeterminate" : false} onCheckedChange={s.toggleAll} onClick={(e) => e.stopPropagation()} aria-label="Select all shown" data-testid={testId} />
);
export const SelectBox = ({ s, id, testId }) => (
  <span onClick={(e) => e.stopPropagation()} className="inline-flex">
    <Checkbox checked={s.sel.has(id)} onCheckedChange={() => s.toggle(id)} aria-label="Select row" data-testid={testId || `select-${id}`} />
  </span>
);

/* Bulk ACTIONS — the same thing a row's own button does, run on every selected row that it applies to.
   action = {
     key, label, icon, danger,
     roles: ["admin", "ops_manager"]            // who may run it (default admin + ops)
     eligible: (doc) => bool                    // rows it applies to; the rest are skipped and counted
     skipNote: "already approved"               // why the others are skipped (shown in the dialog)
     ask: [{ key, label, type: "text"|"textarea"|"password"|"select"|"number", required, min, options: [[v, l]], placeholder }]
     description, confirmLabel,
     run: async (doc, values, user) => {}       // one row; throw to mark it failed (throw skipRow("why") to just skip it)
     once: async (docs, values, user) => {}     // optional: one call for all eligible rows instead of run()
   } */
function BulkActionDialog({ action, docs, nameOf, onClose, onDone }) {
  const { user } = useAuth();
  const [vals, setVals] = useState({});
  const [state, setState] = useState(null); // { done, failed: [], total }
  if (!action) return null;
  const ok = docs.filter(action.eligible || (() => true));
  const skipped = docs.length - ok.length;
  const running = state && state.done + state.failed.length < state.total;
  const finished = state && !running;
  const missing = (action.ask || []).find((f) => f.required && String(vals[f.key] ?? "").trim().length < (f.min || 1));
  const go = async () => {
    if (missing) return toast.error(`${missing.label}${missing.min ? ` (at least ${missing.min} characters)` : ""} is required`);
    const st = { done: 0, failed: [], passed: 0, total: ok.length };
    setState({ ...st });
    if (action.once) {
      try { await action.once(ok, vals, user); st.done = ok.length; } catch (e) { st.failed = ok.map((d) => ({ name: nameOf(d), why: e?.response?.data?.detail || e?.message || "failed" })); }
      setState({ ...st });
    } else {
      for (const d of ok) {
        try { await action.run(d, vals, user); st.done += 1; } catch (e) { if (e?.skip) { st.passed += 1; st.total -= 1; setState({ ...st }); continue; } st.failed.push({ name: nameOf(d), why: e?.code === "permission-denied" ? "Permission denied by Firestore rules" : e?.response?.data?.detail || e?.message || "failed" }); }
        setState({ ...st });
      }
    }
    if (!st.failed.length) { toast.success(`${action.label}: ${st.done} done${skipped + st.passed ? ` · ${skipped + st.passed} skipped` : ""}`); onDone(); }
    else toast.error(`${action.label}: ${st.done} done, ${st.failed.length} failed`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !running && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`bulk-action-${action.key}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{action.label} · {ok.length} of {docs.length}</DialogTitle>
          <DialogDescription>{action.description || "This runs the same action as the button on each row."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-secondary/30 p-2.5 text-xs max-h-32 overflow-y-auto">
            {ok.slice(0, 12).map((d) => <p key={d.id} className="truncate">{nameOf(d)}</p>)}
            {ok.length > 12 && <p className="text-muted-foreground">+{ok.length - 12} more</p>}
            {!ok.length && <p className="text-muted-foreground">None of the selected rows can take this action.</p>}
          </div>
          {skipped > 0 && <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="bulk-action-skipped"><Icons.Info className="h-3.5 w-3.5" />{skipped} selected {skipped === 1 ? "row is" : "rows are"} skipped{action.skipNote ? ` — ${action.skipNote}` : ""}.</p>}
          {!state && (action.ask || []).map((f) => (
            <div key={f.key} className="space-y-1">
              <p className="text-xs font-medium">{f.label}{f.required ? " *" : ""}</p>
              {f.type === "textarea" ? <Textarea rows={2} value={vals[f.key] || ""} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} placeholder={f.placeholder} data-testid={`bulk-ask-${f.key}`} />
                : f.type === "select" ? (
                  <Select value={vals[f.key] || ""} onValueChange={(x) => setVals((v) => ({ ...v, [f.key]: x }))}><SelectTrigger data-testid={`bulk-ask-${f.key}`}><SelectValue placeholder={f.placeholder || "Choose"} /></SelectTrigger>
                    <SelectContent>{(f.options || []).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>)
                  : <Input type={f.type === "password" ? "password" : f.type === "number" ? "number" : "text"} value={vals[f.key] || ""} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} placeholder={f.placeholder} data-testid={`bulk-ask-${f.key}`} />}
            </div>
          ))}
          {state && (
            <div className="space-y-1.5" data-testid="bulk-action-progress">
              <Progress value={state.total ? ((state.done + state.failed.length) / state.total) * 100 : 100} className="h-1.5" />
              <p className="text-xs">{state.done} done{state.failed.length ? ` · ${state.failed.length} failed` : ""}{state.passed ? ` · ${state.passed} skipped` : ""} of {state.total}</p>
              {state.failed.slice(0, 6).map((f, i) => <p key={i} className="text-[11px] text-red-700">{f.name}: {f.why}</p>)}
            </div>
          )}
        </div>
        <DialogFooter>
          {!finished ? (
            <><Button variant="ghost" onClick={onClose} disabled={running}>Cancel</Button>
              <Button variant={action.danger ? "destructive" : "default"} onClick={go} disabled={running || !ok.length} data-testid="bulk-action-confirm">{running ? "Working…" : `${action.confirmLabel || action.label} ${ok.length}`}</Button></>
          ) : <Button onClick={state.failed.length ? onClose : onDone}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const skipRow = (why) => Object.assign(new Error(why), { skip: true });

// Sticky action bar shown while rows are selected: one-click actions + the multi-field bulk editor.
// rows = all loaded docs of `path` (selection is resolved against them).
export function BulkBar({ s, path, rows, fields, label = "records", extra, actions = [], nameOf = (d) => d.name || d.full_name || d.code || d.id }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [act, setAct] = useState(null);
  const docs = useMemo(() => rows.filter((r) => s.sel.has(r.id)), [rows, s.sel]);
  if (!s.count) return null;
  const canEdit = ["admin", "ops_manager"].includes(user?.role);
  const allowed = (a) => (a.roles || ["admin", "ops_manager"]).includes(user?.role);
  return (
    <>
      <div className="sticky top-16 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 shadow-sm backdrop-blur animate-fade-up" data-testid={`bulk-bar-${path}`}>
        <Icons.CheckSquare className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium" data-testid="bulk-selected-count">{s.count} {label} selected</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {actions.map((a) => {
            const n = docs.filter(a.eligible || (() => true)).length;
            const Ic = Icons[a.icon] || Icons.Zap;
            const can = allowed(a);
            return (
              <Button key={a.key} size="sm" variant="outline" disabled={!n || !can} onClick={() => setAct(a)}
                title={!can ? "Not allowed for your role" : !n ? `None selected can be ${a.label.toLowerCase()}` : ""}
                className={cn("h-8 bg-background", a.danger && n && can && "text-red-700 border-red-200 hover:bg-red-50")} data-testid={`bulk-${a.key}`}>
                <Ic className="h-3.5 w-3.5 mr-1" />{a.label}<span className="ml-1 text-[11px] text-muted-foreground tabular-nums">{n}</span>
              </Button>
            );
          })}
          {extra}
          {fields && <Button size="sm" variant={actions.length ? "ghost" : "default"} className="h-8" onClick={() => setOpen(true)} disabled={!canEdit} title={canEdit ? "Set any field to the same value" : "Only admin / ops can bulk update"} data-testid={`bulk-edit-${path}`}><Icons.PenLine className="h-3.5 w-3.5 mr-1" />{actions.length ? "Edit fields" : "Bulk update"}</Button>}
          <Button size="sm" variant="ghost" className="h-8" onClick={s.clear} data-testid="bulk-clear">Clear</Button>
        </div>
      </div>
      {fields && <BulkEditDialog path={path} docs={docs} open={open} fields={fields} onClose={() => setOpen(false)} onDone={() => { setOpen(false); s.clear(); }} />}
      {act && <BulkActionDialog key={act.key} action={act} docs={docs} nameOf={nameOf} onClose={() => setAct(null)} onDone={() => { setAct(null); s.clear(); }} />}
    </>
  );
}
export { BulkActionDialog as ActionDialog };
